import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import Dropdown from "@cayuse/shared-react.components.dropdown";
import IconButton from "@cayuse/shared-react.components.icon-button";
import { crunchClasses } from "@cayuse/shared-react.utils.helpers";
import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import { useTenantFormatter } from "components/BudgetBuilder/shared/TenantFormatter";
import OrgSearchInput from "components/OrgSearchInput";
import Table from "components/Table";
import { REQUIREMENT_DROPDOWN_ITEMS, TYPE_DROPDOWN_OPTIONS } from "globals/constants";
import { fetchSingleOrganization } from "store/actions/picklists";
import { picklistsSelectors, tenantSelectors } from "store/selectors";
import { convertCategoriesToOptions, convertOptionsToTextByValue } from "utils/budgetBuilder";

import { EditableTableCell } from "../../shared/EditableTableCell";
import styles from "./styles.css";


const ExternalCostShareRow = (
	{
		category,
		handleUpdateExternalLineItem,
		isEditing,
		handleRemoveItem,
	},
) => {
	let { hash, unit, value, requirement, type, costShareAmount } = category;
	const { currencyFormatter } = useTenantFormatter();
	const classNames = crunchClasses({
		[styles.row]: true,
	});

	const categories = useSelector(tenantSelectors.proposalCategories);
	const { options: organizations } = useSelector(picklistsSelectors.organizations);

	const categoryOptions = useMemo(
		() => convertCategoriesToOptions(categories),
		[ categories ],
	);
	const categoryTextByValue = useMemo(
		() => convertOptionsToTextByValue(categoryOptions),
		[ categoryOptions ],
	);
	const organizationTextById = useMemo(
		() => convertOptionsToTextByValue(organizations),
		[ organizations ],
	);

	useEffect(() => {
		if (isNonEmptyString(unit) && isNonEmptyArray(organizations)) {
			if (!organizations.find(u => u.value === unit)) {
				fetchSingleOrganization(unit);
			}
		}
	}, [ unit, organizations ]);

	const onOrgFinderChange = payload => handleUpdateExternalLineItem(hash, { unit: payload });

	return (
		<Table.Row
			key={hash}
			id={hash}
			className={classNames}
		>
			<EditableTableCell
				editable={isEditing}
				inputStyles={styles.periodDropdown}
				cellStyles={styles.cellAlignLeft}
				onChange={(e, { value }) => handleUpdateExternalLineItem(hash, { value: value })}
				value={value}
				textFormatter={text => categoryTextByValue[text]}
				renderInput={props => (
					<Dropdown
						{...props}
						selection
						options={categoryOptions}
					/>
				)}
			/>
			<EditableTableCell
				editable={isEditing}
				inputStyles={styles.periodDropdown}
				cellStyles={styles.cellAlignLeft}
				onChange={(e, { value }) => handleUpdateExternalLineItem(hash, { requirement: value })}
				value={requirement}
				renderInput={props => (
					<Dropdown
						selection
						options={REQUIREMENT_DROPDOWN_ITEMS}
						{...props}
					/>
				)}
			/>
			<EditableTableCell
				editable={isEditing}
				inputStyles={styles.orgFinderForm}
				cellStyles={styles.cellAlignLeft}
				onChange={onOrgFinderChange}
				value={unit}
				textFormatter={id => organizationTextById?.[id]}
				renderInput={props => (<OrgSearchInput {...props} />)}
			/>
			<EditableTableCell
				editable={isEditing}
				inputStyles={styles.periodDropdown}
				cellStyles={styles.cellAlignLeft}
				onChange={(e, { value }) => handleUpdateExternalLineItem(hash, { type: value })}
				value={type}
				renderInput={props => (
					<Dropdown
						selection
						options={TYPE_DROPDOWN_OPTIONS}
						{...props}
					/>
				)}
			/>
			<EditableTableCell
				editable={isEditing}
				cellStyles={`${styles.cellAlignRight} ${styles.valueCellPadding}`}
				onChange={event => handleUpdateExternalLineItem(hash, { costShareAmount: event.target.value })}
				value={costShareAmount}
				textFormatter={text => currencyFormatter(text)}
			/>
			<Table.Cell className={styles.cellAlignCenter}>
				{isEditing && (
					<IconButton
						tertiary
						onClick={() => handleRemoveItem(hash)}
						className={styles.deleteButton}
						icon="trash"
					/>
				)}
			</Table.Cell>
		</Table.Row>
	);
};

export default ExternalCostShareRow;
